import moment from 'moment';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { AllFund } from '@/api/fund/queries';
import { FundApprovalStatus, fundApprovalStatus } from '@/api/fund/types';
import DefaultCover from '@/assets/png/fund-dashboard/hands.png';
import VerifiedIcon from '@/assets/svg/ui-elements/check-verified.svg';
import ClockIcon from '@/assets/svg/ui-elements/clock-filled-blue.svg';
import XCloseIcon from '@/assets/svg/ui-elements/close-circle-red.svg';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import EyeIcon from '@/assets/svg/ui-elements/eye-slash-gray.svg';
import TimerIcon from '@/assets/svg/ui-elements/hourglass-red.svg';
import ManageIcon from '@/assets/svg/ui-elements/manage.svg';
import OfficeIcon from '@/assets/svg/ui-elements/office-blue.svg';
import SecurityTimeIcon from '@/assets/svg/ui-elements/security-time.svg';
import UsersIcon from '@/assets/svg/ui-elements/users.svg';
import { getSetupFundReviewOrEditPath } from '@/pages/setup-fund/utils/fund-review-routing';
import { SuspendedFundAlert } from '@/shared/components/suspended-fund-alert';
import Badge from '@/shared/design-system/components/Badge';
import { BadgeColor } from '@/shared/design-system/components/Badge/types';
import Button from '@/shared/design-system/components/Button';
import Tooltip from '@/shared/design-system/components/Tooltip';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { statusVisual } from '@/utils/fund-status-visual';

import {
  ActionsRow,
  CardRoot,
  CoverImage,
  Divider,
  EntityNameText,
  FeedersPill,
  HeaderRow,
  HeaderTextCol,
  MetricLabel,
  MetricRow,
  MetricsCol,
  MetricValue,
  NameText,
  StatusChip,
  StatusRow,
} from './fund-dashboard-card.styled';

type Props = { fund: AllFund & { feedersCount?: number } };

const verificationBadge: Record<FundApprovalStatus, { label: string; color: BadgeColor }> = {
  [fundApprovalStatus.Verified]: { label: 'Verified', color: 'primary' },
  [fundApprovalStatus.AwaitingApproval]: { label: 'Awaiting Approval', color: 'yellow' },
  [fundApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    color: 'orange',
  },
  [fundApprovalStatus.Rejected]: { label: 'Rejected', color: 'destructive' },
  [fundApprovalStatus.Blocked]: { label: 'Blocked', color: 'destructive' },
  [fundApprovalStatus.Draft]: { label: 'Draft', color: 'default' },
};

// ─── Legacy exports preserved for backward-compatibility with other pages ────

export const investmentAvailabilityStatusStyles = {
  Open: {
    status: 'Open',
    statusBg: '#2FAB73',
    icon: OfficeIcon,
    labelColor: '#AED3FF',
  },
  Closed: {
    status: 'Closed',
    statusBg: '#70353E',
    icon: XCloseIcon,
    labelColor: '#E6483D',
  },
  PreLaunch: {
    status: 'Pre-launch',
    statusBg: '#9437FF',
    icon: ClockIcon,
    labelColor: '#AED3FF',
  },
  None: {
    status: 'No Status',
    statusBg: '#F9D151',
    icon: null,
    labelColor: '#F9D151',
  },
  Hidden: {
    status: 'Hidden',
    statusBg: '#F9D151',
    icon: EyeIcon,
    labelColor: '#BDBEC2',
  },
  Private: {
    status: 'Private',
    statusBg: '#686b7f',
    icon: null,
    labelColor: '#686b7f',
  },
} as const;

export const fundApprovalStatusStyles = {
  [fundApprovalStatus.Verified]: {
    label: 'Verified',
    color: '#39CA88',
    icon: VerifiedIcon,
  },
  [fundApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    color: '#FFAA5A',
    icon: TimerIcon,
  },
  [fundApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    color: '#F5841F',
    icon: SecurityTimeIcon,
  },
  [fundApprovalStatus.Draft]: {
    label: 'Draft',
    color: '#F9D151',
    icon: null,
  },
  [fundApprovalStatus.Rejected]: {
    label: 'Rejected',
    color: '#E6483D',
    icon: XCloseIcon,
  },
  [fundApprovalStatus.Blocked]: {
    label: 'Blocked',
    color: '#E6483D',
    icon: null,
  },
};

// ─────────────────────────────────────────────────────────────────────────────

export type IAKey = 'Open' | 'Closed' | 'PreLaunch' | 'None' | 'Hidden' | 'Private';

export function normalizeIAStatus(v: unknown): IAKey {
  return v === 'Open' ||
    v === 'Closed' ||
    v === 'PreLaunch' ||
    v === 'None' ||
    v === 'Hidden' ||
    v === 'Private'
    ? v
    : 'None';
}

const FundDashboardCard: FC<Props> = ({ fund }) => {
  const navigate = useNavigate();
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const isAdminSession = selectedUserRole === 'admin';

  const coverSrc = fund.cardImageUrl || DefaultCover;
  const name = fund.name || '';
  const entityName = fund.entity?.name || '';
  const aum = fund.aumInMillionsUsd
    ? `$${(fund.aumInMillionsUsd * 1_000_000).toLocaleString('en-US')}`
    : '$0';
  const lastUpdated = fund.updatedAt ? moment(fund.updatedAt).format('MMM D, YYYY') : '—';

  const iaKey = normalizeIAStatus(fund.investmentAvailabilityStatus);
  const visual = statusVisual[iaKey] ?? statusVisual.None;
  const StatusIcon = visual.icon;
  const badge = verificationBadge[fund.fundApprovalStatus];

  const feedersCount = fund.feedersCount ?? 0;

  const reviewHref = getSetupFundReviewOrEditPath({
    id: fund.id,
    canReviewCrossEntity: isAdminSession,
    fundApprovalStatus: fund.fundApprovalStatus,
    draftStatus: fund.draftStatus,
  });

  return (
    <CardRoot>
      <HeaderRow>
        <CoverImage $src={coverSrc} />

        <HeaderTextCol>
          <div>
            <Tooltip title={name} disabled={name.length < 40}>
              <NameText>{name}</NameText>
            </Tooltip>
            {entityName && <EntityNameText>{entityName}</EntityNameText>}
          </div>

          {feedersCount > 0 && (
            <FeedersPill>
              <UsersIcon width={14} height={14} />
              {feedersCount} feeders
            </FeedersPill>
          )}
        </HeaderTextCol>
      </HeaderRow>

      <Divider />

      <StatusRow>
        <StatusChip $color={visual.color}>
          {StatusIcon && <StatusIcon width={14} height={14} />}
          {visual.label}
        </StatusChip>
        {badge && <Badge label={badge.label} color={badge.color} variant="solid" />}
        <SuspendedFundAlert
          fundApprovalStatus={fund.fundApprovalStatus}
          entityApprovalStatus={fund.entity?.entityApprovalStatus}
        />
      </StatusRow>

      <MetricsCol>
        <MetricRow>
          <MetricLabel>Units (Total)</MetricLabel>
          <MetricValue>{(fund.unitsIssued ?? 0).toLocaleString('en-US')}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Fund AUM</MetricLabel>
          <MetricValue>{aum}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Investors</MetricLabel>
          <MetricValue>{fund.investors ?? 0}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Last Updated</MetricLabel>
          <MetricValue>{lastUpdated}</MetricValue>
        </MetricRow>
      </MetricsCol>

      <Divider />

      <ActionsRow>
        <Button color="tertiary" variant="outline" onClick={() => navigate(reviewHref)}>
          <EditIcon width={16} height={16} style={{ marginRight: 8 }} />
          Review/Edit
        </Button>
        <Button
          color="tertiary"
          variant="outline"
          onClick={() => navigate(`/fund-dashboard/${fund.id}`)}
        >
          <ManageIcon width={16} height={16} style={{ marginRight: 8 }} />
          Manage
        </Button>
      </ActionsRow>
    </CardRoot>
  );
};

export default FundDashboardCard;
